import { useTranslation } from 'react-i18next';
import { Loader2, Pencil, Unlink } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { useClient } from '@/api/hooks';
import type { ClientDetail } from '@/api/types';

interface ClientDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string | null;
  onUnlink: () => void;
  onEdit: (client: ClientDetail) => void;
  getAccessToken: () => Promise<string>;
}

function ClientDetailContent({
  data,
  onEdit,
  onUnlink,
  t,
}: Readonly<{
  data: ClientDetail;
  onEdit: (client: ClientDetail) => void;
  onUnlink: () => void;
  t: (key: string) => string;
}>) {
  return (
    <div className="space-y-4 p-4">
      {/* Contact info - key-value layout */}
      <div className="space-y-2">
        {data.name && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t('clients.name')}</span>
            <span className="font-medium">{data.name}</span>
          </div>
        )}
        {data.address && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t('clients.address')}</span>
            <span className="font-medium">{data.address}</span>
          </div>
        )}
        {data.phone && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t('clients.phone')}</span>
            <span className="font-medium">{data.phone}</span>
          </div>
        )}
        {data.email && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t('clients.email')}</span>
            <span className="font-medium">{data.email}</span>
          </div>
        )}
        {data.reg_code && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t('clients.regCode')}</span>
            <span className="font-medium">{data.reg_code}</span>
          </div>
        )}
        {data.vat_number && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t('clients.vatNumber')}</span>
            <span className="font-medium">{data.vat_number}</span>
          </div>
        )}
        {data.notes && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{t('clients.notes')}</span>
            <span className="font-medium">{data.notes}</span>
          </div>
        )}
      </div>

      {/* Quick actions */}
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onEdit(data)}
          data-testid="client-edit-button"
        >
          <Pencil className="mr-1 h-4 w-4" />
          {t('clients.edit')}
        </Button>
        <Button variant="outline" size="sm" onClick={onUnlink} data-testid="client-unlink-button">
          <Unlink className="mr-1 h-4 w-4" />
          {t('clients.unlink')}
        </Button>
      </div>

      {/* Quote history */}
      <div>
        <h4 className="mb-2 text-sm font-semibold">{t('clients.quoteHistory')}</h4>
        {data.quotes.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('clients.noQuotes')}</p>
        ) : (
          <div className="space-y-2">
            {data.quotes.map((quote) => (
              <Card key={quote.id}>
                <CardContent className="flex items-center justify-between p-3">
                  <div>
                    <Badge variant="secondary">{quote.status}</Badge>
                    <span className="ml-2 text-sm">
                      {new Date(quote.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <span className="font-medium">&euro;{(quote.grand_total ?? 0).toFixed(2)}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function ClientDetailDrawer({
  isOpen,
  onClose,
  clientId,
  onUnlink,
  onEdit,
  getAccessToken,
}: Readonly<ClientDetailDrawerProps>) {
  const { t } = useTranslation();
  const { data, isLoading } = useClient(getAccessToken, isOpen ? clientId : null);

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center p-8" data-testid="client-detail-loading">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      );
    }
    if (data) {
      return <ClientDetailContent data={data} onEdit={onEdit} onUnlink={onUnlink} t={t} />;
    }
    return null;
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{t('clients.detail')}</SheetTitle>
          <SheetDescription className="sr-only">
            Client information and quote history
          </SheetDescription>
        </SheetHeader>
        {renderBody()}
      </SheetContent>
    </Sheet>
  );
}
